import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { setGroupNotifications } from '../../../lib/group-chat-api';
import {
  markMaestroHintSeen,
  modeLabelKey,
  notificationOptions,
  readMaestroHintSeen,
  shouldShowMaestroDecidesHint,
  type GroupNotificationMode,
  type GroupNotifications,
  type NotificationChoice,
} from '../../../lib/group-notifications';
import { useGroupMaestroName, useGroupNotifications } from '../../../lib/use-group-summary';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Modal } from '../../Modal';
import { Button, Card, IconButton, RadioGroup, Switch, Typography } from '../ui';

/**
 * The caller's own NOTIFICATIONS for one group (rules: `lib/group-notifications.ts`; iOS `GroupNotificationsSheet`,
 * Android `GroupNotificationsSheet` draw the same): All messages, Mentions only, Let my Maestro decide (only while
 * the caller's own Maestro is in the group), Use default (naming which default applies), and Mute — mentions and
 * replies still notify. Each choice saves at once through `viewer-state`; a refusal puts the old one back.
 */
export function GroupNotificationsDialog({
  conversationId,
  notifications,
  onClose,
}: {
  conversationId: string;
  notifications: GroupNotifications;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // The Maestro as THIS group names it ("Marco's Maestro"), never the generic default.
  const maestroAgentName = useGroupMaestroName(conversationId);
  const { logModalActionBi } = useGenericBi();
  const [current, setCurrent] = useState(notifications);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Opening the picker is "seen" for the one-time hint.
  useEffect(() => markMaestroHintSeen(conversationId), [conversationId]);

  const choose = async (choice: NotificationChoice, action: string) => {
    if (saving) return;
    logModalActionBi({ page_name: 'chat', modal_name: 'group_notifications', action } as never);
    setSaving(true);
    setError(null);
    const result = await setGroupNotifications(conversationId, current, choice);
    setSaving(false);
    setCurrent(result.notifications);
    if (result.errorKey) setError(t(result.errorKey, { maestroAgentName }));
  };

  const options = notificationOptions(current);
  const label = (mode: GroupNotificationMode) => t(modeLabelKey(mode), { maestroAgentName });
  const checked = options.find((o) => o.checked)?.choice;
  const value = checked?.kind === 'mode' ? checked.mode : 'DEFAULT';

  return (
    <Modal open onClose={onClose} title={t('chat.group.notify.title')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
      <div data-testid="group-notifications" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <RadioGroup<GroupNotificationMode | 'DEFAULT'>
          aria-label={t('chat.group.notify.title')}
          value={value}
          disabled={saving}
          onChange={(next) =>
            void (next === 'DEFAULT'
              ? choose({ kind: 'default' }, 'notify_default')
              : choose({ kind: 'mode', mode: next }, `notify_${next.toLowerCase()}`))
          }
          options={options.map((o) => ({
            value: o.choice.kind === 'mode' ? o.choice.mode : 'DEFAULT',
            label: o.choice.kind === 'mode' ? label(o.choice.mode) : t(o.labelKey),
            ...(o.hint ? { description: o.hint.mode ? t(o.hint.key, { mode: label(o.hint.mode) }) : t(o.hint.key, { maestroAgentName }) } : {}),
          }))}
        />
        <Switch
          checked={current.muted}
          disabled={saving}
          onChange={(next) => void choose({ kind: 'mute', muted: next }, next ? 'mute' : 'unmute')}
          label={t('chat.group.mute')}
          description={t('chat.group.notify.muteNote')}
        />
        {error && (
          <Typography variant="p2" component="p" role="alert" data-testid="group-notifications-error" style={{ color: 'var(--ds-text-destructive)' }}>
            {error}
          </Typography>
        )}
      </div>
    </Modal>
  );
}

/**
 * The one-time inline hint above a group's composer after the caller's Maestro joined and the mode silently became
 * "Maestro decides". Shown once per group per device: it is marked seen the first time it is drawn, and stays up
 * here until dismissed or the chat is left. Tapping it opens the Notifications picker.
 */
export function GroupNotifyHint({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation();
  // The Maestro as THIS group names it ("Marco's Maestro"), never the generic default.
  const maestroAgentName = useGroupMaestroName(conversationId);
  const { logPageClickBi } = useGenericBi();
  const notifications = useGroupNotifications(conversationId);
  const [shownFor, setShownFor] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [picking, setPicking] = useState(false);
  const eligible = shouldShowMaestroDecidesHint(notifications, readMaestroHintSeen(), conversationId);

  useEffect(() => {
    setDismissed(false);
    setShownFor(null);
  }, [conversationId]);
  useEffect(() => {
    if (eligible && shownFor !== conversationId) {
      setShownFor(conversationId);
      markMaestroHintSeen(conversationId);
    }
  }, [eligible, shownFor, conversationId]);

  const visible = shownFor === conversationId && !dismissed;
  if (!visible && !picking) return null;
  return (
    <>
      {visible && (
        <Card variant="sunken" size="sm" data-testid="group-notify-hint" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <BellRing size={16} aria-hidden />
            <Typography variant="p2" color="strong" component="span">
              {t('chat.group.notify.switchedHint', { maestroAgentName })}
            </Typography>
          </span>
          <Button
            size="small"
            variant="outlined"
            data-testid="group-notify-hint-open"
            onClick={() => {
              logPageClickBi({ page_name: 'chat', button_name: 'group_notify_hint_open' });
              setPicking(true);
            }}
          >
            {t('chat.group.notify.title')}
          </Button>
          <IconButton
            size="small"
            aria-label={t('chat.group.notify.dismiss')}
            data-testid="group-notify-hint-dismiss"
            onClick={() => {
              logPageClickBi({ page_name: 'chat', button_name: 'group_notify_hint_dismiss' });
              setDismissed(true);
            }}
          >
            <X size={16} aria-hidden />
          </IconButton>
        </Card>
      )}
      {picking && notifications && (
        <GroupNotificationsDialog
          conversationId={conversationId}
          notifications={notifications}
          onClose={() => {
            setPicking(false);
            setDismissed(true);
          }}
        />
      )}
    </>
  );
}
